import { codeableConcept } from '@seen/core/codeableConcept'
import { ExtendedObservation } from '@seen/core/models/observation'
import { cn } from '@seen/ui'
import type { NodeViewProps } from '@tiptap/react'
import { type JSX, useEffect, useState } from 'react'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import {
  ObservationNoValue,
  ObservationWrapper,
} from '#src/components/rich-text-editor/ObservationNode/ObservationWrapper.tsx'
import type {
  ObservationNodeAttributes,
  ObservationNodeOptions,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useOptionalParticipantReference } from '#src/contexts/current-participant/useOptionalParticipantReference.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export function ObservationNodeTextInput(props: NodeViewProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const attributes = props.node.attrs as ObservationNodeAttributes

  const extensionOptions = props.extension.options as ObservationNodeOptions

  const currentParticipant = useOptionalParticipantReference()
  const participant = extensionOptions.participant ?? currentParticipant

  const practitioner = useCurrentPractitioner()
  const createMutation = useCreateMutation<ExtendedObservation>()

  const [loading, setLoading] = useState(false)

  const [value, setValue] = useState<string | null>(null)

  useEffect(() => {
    if (attributes.autofocus) {
      props.updateAttributes({ autofocus: false })
    }
  }, [attributes.autofocus, props])

  if (!props.editor.isEditable) {
    return <ObservationNoValue label={attributes.codingDisplay} />
  }

  return (
    <ObservationWrapper className="pr-px">
      <span>{attributes.codingDisplay}:&nbsp;</span>
      <div
        className="mr-0.75" // Accounts for the shift below.
      />
      <ObservationRichTextEditor
        placeholder="Add text…"
        disabled={loading || isReadOnly}
        onChange={setValue}
        autofocus={attributes.autofocus ? 'end' : false}
        onBlur={async () => {
          if (!value?.trim()) {
            return
          }
          setLoading(true)
          const createdObservation = await createMutation.mutateAsync(
            ExtendedObservation.fromValueString({
              value,
              code: codeableConcept({
                code: attributes.code,
                system: attributes.system,
                display: attributes.codingDisplay,
              }),
              participant,
              practitioner: practitioner.reference,
            })
          )
          props.updateAttributes({
            reference: createdObservation.reference.reference,
          })
          extensionOptions.onReferencePersisted?.()
          setLoading(false)
        }}
        className={cn(
          'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
          '-ml-0.75' // For visual alignment when the content goes onto multiple lines.
        )}
      />
    </ObservationWrapper>
  )
}
